/**
 * Admin CRUD for branch directory (counter ops).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Building2, Loader2, Plus, X, CheckCircle, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/branches`;

function Toast({ kind, text, onDismiss }) {
  if (!text) return null;
  const tone = kind === 'ok'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  const Icon = kind === 'ok' ? CheckCircle : AlertTriangle;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${tone}`}>
      <Icon className="w-4 h-4 mt-0.5" />
      <div className="flex-1">{text}</div>
      <button onClick={onDismiss}><X className="w-4 h-4" /></button>
    </div>
  );
}

function BranchModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', city: '', state: '', gst_state_code: '', address: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setErr(null);
      setForm({
        name: initial?.name || '',
        city: initial?.city || '',
        state: initial?.state || '',
        gst_state_code: initial?.gst_state_code || '',
        address: initial?.address || '',
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (initial?.id) {
        await axios.put(`${API}/${initial.id}`, form, { withCredentials: true });
      } else {
        await axios.post(API, form, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-[#1B375B] mb-4">
          {initial?.id ? 'Edit branch' : 'Add branch'}
        </h3>
        <div className="space-y-3">
          {['name', 'city', 'state', 'gst_state_code', 'address'].map((field) => (
            <div key={field}>
              <label className="text-[10px] uppercase font-semibold text-gray-500">{field.replace('_', ' ')}</label>
              <input
                value={form[field]}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          ))}
          {err && <p className="text-sm text-rose-700">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-lg">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !form.name || !form.city || !form.state}
            className="px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BranchManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [toast, setToast] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, { withCredentials: true });
      setRows(res.data.rows || []);
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed to load branches' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this branch?')) return;
    try {
      await axios.put(`${API}/${id}`, { is_active: false }, { withCredentials: true });
      setToast({ kind: 'ok', text: 'Branch deactivated' });
      fetchRows();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="branch-management">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Building2 className="w-8 h-8 text-[#1B375B]" />
          <div>
            <h1 className="text-2xl font-bold text-[#1B375B]">Branches</h1>
            <p className="text-sm text-gray-500">Multi-branch directory for counter operations</p>
          </div>
        </div>
        <button
          onClick={() => setModal({ open: true, initial: null })}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm font-semibold"
          data-testid="branch-add-btn"
        >
          <Plus className="w-4 h-4" /> Add branch
        </button>
      </div>

      {toast && <div className="mb-4"><Toast {...toast} onDismiss={() => setToast(null)} /></div>}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#659A9B]" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">State</th>
                <th className="px-4 py-3">GST Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 font-semibold text-[#1B375B]">{r.name}</td>
                  <td className="px-4 py-3">{r.city}</td>
                  <td className="px-4 py-3">{r.state}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.gst_state_code || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                      {r.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 space-x-2">
                    <button
                      onClick={() => setModal({ open: true, initial: r })}
                      className="text-xs text-[#659A9B] font-semibold hover:underline"
                    >
                      Edit
                    </button>
                    {r.is_active !== false && (
                      <button onClick={() => deactivate(r.id)} className="text-xs text-rose-600 font-semibold hover:underline">
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No branches yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <BranchModal
        open={modal.open}
        initial={modal.initial}
        onClose={() => setModal({ open: false, initial: null })}
        onSaved={() => {
          setModal({ open: false, initial: null });
          setToast({ kind: 'ok', text: 'Branch saved' });
          fetchRows();
        }}
      />
    </div>
  );
}
